import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Register font
Font.register({
  family: 'Helvetica-Bold',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
});

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },
  header: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#1E40AF',
    paddingBottom: 10,
  },
  logoText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginBottom: 4,
  },
  logoSubtext: {
    fontSize: 12,
    color: '#3B82F6',
    textAlign: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginBottom: 10,
    fontFamily: 'Helvetica-Bold',
  },
  subtitle: {
    fontSize: 14,
    color: '#4B5563',
    marginBottom: 30,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E40AF',
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 5,
  },
  loanType: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#111827',
    marginTop: 15,
    marginBottom: 5,
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginTop: 10,
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#1E40AF',
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    fontSize: 10,
    flex: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 10,
    color: '#6B7280',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 10,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: '#6B7280',
  },
});

// Loan types data
const loanTypes = [
  {
    name: 'Personal Loan',
    minIncome: '₹25,000/month',
    minCIBIL: '750+',
    documents: [
      'ID Proof (Aadhaar, PAN, Passport, Voter ID, or Driving License)',
      'Address Proof (Utility bill, Aadhaar, Passport, or Voter ID)',
      'Last 3 months salary slips',
      'Last 6 months bank statements',
      '2 passport size photographs',
      'Employment proof (for salaried)'
    ],
    notes: 'Self-employed individuals need ITR for last 2 years and business proof.'
  },
  {
    name: 'Home Loan',
    minIncome: '₹35,000/month',
    minCIBIL: '700+',
    documents: [
      'ID Proof (Aadhaar, PAN, Passport, Voter ID, or Driving License)',
      'Address Proof (Utility bill, Aadhaar, Passport, or Voter ID)',
      'Last 6 months salary slips (salaried)',
      'Last 2 years ITR (self-employed)',
      'Last 6 months bank statements',
      'Property documents',
      'Sale agreement',
      'NOC from society/authority'
    ],
    notes: 'Additional documents may be required based on property type and location.'
  },
  {
    name: 'Business Loan',
    minIncome: '₹50,000/month (business income)',
    minCIBIL: '680+',
    documents: [
      'Business registration certificate',
      'Business address proof',
      'Last 3 years ITR with computation of income',
      'Last 3 years balance sheet and profit & loss account',
      'Bank statements for last 12 months',
      'Business license',
      'Partnership deed (if applicable)'
    ],
    notes: 'Additional collateral may be required for higher loan amounts.'
  },
  {
    name: 'Education Loan',
    minIncome: 'Varies by lender',
    minCIBIL: '750+ (co-applicant)',
    documents: [
      'Admission letter from the institution',
      'Fee structure',
      'ID and address proof of student and co-applicant',
      'Income proof of co-applicant',
      'Academic records',
      'Bank statements of co-applicant'
    ],
    notes: 'Co-applicant (parent/guardian) is mandatory. Moratorium period available.'
  },
  {
    name: 'Car Loan',
    minIncome: '₹25,000/month',
    minCIBIL: '700+',
    documents: [
      'ID Proof (Aadhaar, PAN, Passport, Voter ID, or Driving License)',
      'Address Proof (Utility bill, Aadhaar, Passport, or Voter ID)',
      'Last 3 months salary slips',
      'Last 6 months bank statements',
      'Driving License',
      'Car quotation from dealer'
    ],
    notes: 'Car will be hypothecated to the bank until loan closure.'
  }
];

const LoanEligibilityGuide = () => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.logoText}>White Pearl Finance</Text>
        <Text style={styles.logoSubtext}>Your Trusted Financial Partner</Text>
        <Text style={{ fontSize: 14, color: '#6B7280', marginTop: 10 }}>Loan Eligibility Guide</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Understanding Loan Eligibility</Text>
        <Text>
          Loan eligibility is determined based on several factors including your income, credit score, existing debts, 
          employment stability, and the type of loan you're applying for. This guide provides detailed information 
          about the requirements for different types of loans to help you prepare your application.
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>General Eligibility Criteria</Text>
        <Text>
          While specific requirements vary by loan type and lender, most loans require:
        </Text>
        <Text>
          • Indian citizenship or valid residency status
          • Minimum age of 21 years (18 for some student loans)
          • Maximum age of 60-65 years at loan maturity
          • Stable source of income
          • Good credit history (CIBIL score)
        </Text>
      </View>

      {loanTypes.map((loan, index) => (
        <View key={index} style={styles.section}>
          <Text style={styles.loanType}>{loan.name}</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, {flex: 2}]}>Parameter</Text>
              <Text style={styles.tableCell}>Details</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, {flex: 2}]}>Minimum Income</Text>
              <Text style={styles.tableCell}>{loan.minIncome}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, {flex: 2}]}>Minimum CIBIL Score</Text>
              <Text style={styles.tableCell}>{loan.minCIBIL}</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, {flex: 2}]}>Documents Required</Text>
              <View style={styles.tableCell}>
                {loan.documents.map((doc, i) => (
                  <Text key={i}>• {doc}</Text>
                ))}
              </View>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, {flex: 2}]}>Notes</Text>
              <Text style={styles.tableCell}>{loan.notes}</Text>
            </View>
          </View>
        </View>
      ))}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>How to Improve Your Eligibility</Text>
        <Text>
          1. Maintain a good credit score (750+ is ideal)
          2. Keep your credit utilization below 30%
          3. Avoid multiple loan applications in a short span
          4. Maintain a stable job/business
          5. Clear existing debts before applying
          6. Have a good relationship with your bank
        </Text>
      </View>

      <View style={styles.footer}>
        <Text>This document is for informational purposes only. Actual terms may vary by lender.</Text>
        <Text>Generated on: {new Date().toLocaleDateString()}</Text>
      </View>
      
      <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </Page>
  </Document>
);

export default LoanEligibilityGuide;
